import { styled } from "@mui/material/styles";
import { Box, Button, ButtonProps, Chip, Typography } from "@mui/material";
import { IMAGE_BORDER_RADIUS } from "./constants";

// ── CommonBoxStyles (moved from ItemMaintenanceForm) ─────────────────
export const CommonBoxStyles = styled(Box)(({ theme }) => ({
  background: theme.palette.background.default,
  borderRadius: theme.spacing(1),
}));

// ── Page-level containers ────────────────────────────────────────────
export const PageContentBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(4),
  borderRadius: theme.spacing(4),
}));

export const SectionBox = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[100],
  padding: theme.spacing(4),
}));

// ── Flex layouts ─────────────────────────────────────────────────────
export const FlexCenterBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
});

export const FlexEndBox = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
});

export const FlexBetweenBox = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

// ── DailyBudgetPage ──────────────────────────────────────────────────
export const DatePickerWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  background: theme.palette.background.paper,
  width: 300,
  padding: theme.spacing(2),
  margin: "0 auto",
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(1),
}));

export const NavigationRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const FlexStartBox = styled(Box)({
  display: "flex",
  justifyContent: "flex-start",
});

export const TransactionsRow = styled(Box)({
  display: "flex",
  flexDirection: "row",
});

export const DesktopOnlyMessageBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(4),
  textAlign: "center",
}));

export const SpacedSectionBox = styled(Box)(({ theme }) => ({
  background: theme.palette.grey[100],
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

// ── ProcessOrderPage ─────────────────────────────────────────────────
export const ContactFormContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.text.primary,
  padding: theme.spacing(1),
}));

export const WhatsAppButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.brand.whatsapp,
  color: theme.palette.common.white,
  textTransform: "none",
  padding: theme.spacing(2),
  margin: theme.spacing(2),
  "&:hover": {
    backgroundColor: theme.palette.brand.whatsappHover,
  },
}));

/**
 * The persistent CTA in the sticky bottom bar.
 *
 * Uses `brand.whatsappCta` rather than `brand.whatsapp`: the official green sits on white
 * everywhere else in the app, and against the bar's navy it reads as neon.
 */
// Typed as an anchor: `styled()` drops MUI's `OverridableComponent` generic, so without this the
// `component="a"` / `href` / `target` the call site needs would not typecheck.
export const FooterWhatsAppButton = styled(Button)<ButtonProps<"a">>(({ theme }) => ({
  backgroundColor: theme.palette.brand.whatsappCta,
  color: theme.palette.common.white,
  textTransform: "none",
  fontWeight: 600,
  whiteSpace: "nowrap",
  borderRadius: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingTop: theme.spacing(1.25),
  paddingBottom: theme.spacing(1.25),
  "&:hover": {
    backgroundColor: theme.palette.brand.whatsappCtaHover,
  },
}));

// ── Discount display ─────────────────────────────────────────────────
export const DiscountBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  fontWeight: "bold",
  height: 22,
  fontSize: "0.75rem",
}));

/**
 * The price before the discount, on a product card, the detail page and the order receipt.
 *
 * The strike-through is what demotes it, not a fade: `opacity: 0.6` over the navy body colour
 * landed around 3.6:1, and this is a number the shopper is meant to read and compare against the
 * one beside it (EB-554). `text.secondary` says the same thing and stays legible.
 */
export const OriginalPrice = styled(Typography)(({ theme }) => ({
  textDecoration: "line-through",
  color: theme.palette.text.secondary,
}));

// ── Service pages ────────────────────────────────────────────────────
export const ServiceImage = styled("img")({
  width: "100%",
  borderRadius: IMAGE_BORDER_RADIUS,
});

// ── Policy pages ─────────────────────────────────────────────────────
export const AnchorSpan = styled("span")({
  position: "relative",
  top: -80,
});

// ── Form components ──────────────────────────────────────────────────
export const SaveActionBar = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: theme.spacing(2),
  marginTop: theme.spacing(3),
  display: "flex",
  justifyContent: "flex-end",
  boxShadow: theme.shadows[2],
  backgroundColor: theme.palette.background.default,
}));

export const ImageUploadBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.spacing(2),
}));

export const FormFieldGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gap: theme.spacing(2),
}));

export const FormFieldGridResponsive = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// ── LocationPage ─────────────────────────────────────────────────────
export const LocationContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
}));

// ── NotFoundPage ─────────────────────────────────────────────────────
export const NotFoundContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

// ── HomePage ─────────────────────────────────────────────────────────
export const ServicesContainer = styled(Box)(({ theme }) => ({
  boxShadow: theme.shadows[4],
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));
